/**
 * Nhật ký hoạt động của luồng — bộ lọc kết quả (nhiều lựa chọn) · thời lượng ·
 * khoảng ngày · Làm mới, bảng lần chạy, bấm một dòng để xem vết từng bước.
 * Dùng ở hộp "Nhật ký" trong trình dựng luồng và ở tab Nhật ký của danh sách.
 */
import { useMemo, useState } from 'react'
import { History, RefreshCw, Trash2, X, Clock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { EmptyState } from '@/components/shared/feedback'
import { cn } from '@/lib/utils'
import { storeV2, useStoreV2 } from '@/hooks/use-automation-v2'
import type { WorkflowRun } from '@/lib/automation-v2/types'
import {
  KET_QUA_LABELS, THOI_LUONG_LABELS, ketQuaLanChay, locLanChay, locMacDinh, moTaThoiLuong,
  type KetQuaChay, type ThoiLuong,
} from '@/lib/automation-v2/nhat-ky'
import { ChiTietLanChay } from './chi-tiet-lan-chay'

const fmt = (s?: string) => (s ? new Date(s).toLocaleString('vi-VN', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit', year: 'numeric' }) : '—')

export function NhatKyHoatDong({ workflowId, hienTenLuong = true }: { workflowId?: string; hienTenLuong?: boolean }) {
  const { runs } = useStoreV2()
  const [loc, setLoc] = useState(() => locMacDinh(workflowId))
  const [lamMoi, setLamMoi] = useState(0)
  const [mo, setMo] = useState<WorkflowRun | null>(null)
  const hien = useMemo(() => locLanChay(runs, { ...loc, workflowId }), [runs, loc, workflowId, lamMoi]) // eslint-disable-line react-hooks/exhaustive-deps
  const tatCaKetQua = Object.keys(KET_QUA_LABELS) as KetQuaChay[]
  const daChon = loc.ketQua
  const doiKetQua = (k: KetQuaChay, v: boolean) => setLoc((f) => ({ ...f, ketQua: v ? [...f.ketQua, k] : f.ketQua.filter((x) => x !== k) }))

  return (
    <div className="space-y-3">
      {/* Thanh lọc */}
      <div className="flex flex-wrap items-center gap-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-9 gap-1.5 font-normal">
              {daChon.length === tatCaKetQua.length ? 'Mọi kết quả' : daChon.length === 0 ? 'Chưa chọn kết quả' : (
                <><span className="rounded bg-muted px-1.5 py-0.5 text-[11px]">{KET_QUA_LABELS[daChon[0]].ten}</span>{daChon.length > 1 && <span className="text-[11px] text-muted-foreground">+{daChon.length - 1}</span>}</>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            {tatCaKetQua.map((k) => (
              <DropdownMenuCheckboxItem key={k} checked={daChon.includes(k)} onCheckedChange={(v) => doiKetQua(k, !!v)} onSelect={(e) => e.preventDefault()}>
                {KET_QUA_LABELS[k].ten}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <Select value={loc.thoiLuong} onValueChange={(v) => setLoc((f) => ({ ...f, thoiLuong: v as ThoiLuong }))}>
          <SelectTrigger className="h-9 w-44 text-xs"><SelectValue placeholder="Thời lượng" /></SelectTrigger>
          <SelectContent>{(Object.keys(THOI_LUONG_LABELS) as ThoiLuong[]).map((t) => <SelectItem key={t} value={t}>{THOI_LUONG_LABELS[t]}</SelectItem>)}</SelectContent>
        </Select>

        <div className="flex items-center gap-1 rounded-md border px-2">
          <Input type="date" value={loc.tu} onChange={(e) => setLoc((f) => ({ ...f, tu: e.target.value }))} className="h-8 w-[8.5rem] border-0 px-1 text-xs shadow-none focus-visible:ring-0" />
          <span className="text-muted-foreground">–</span>
          <Input type="date" value={loc.den} onChange={(e) => setLoc((f) => ({ ...f, den: e.target.value }))} className="h-8 w-[8.5rem] border-0 px-1 text-xs shadow-none focus-visible:ring-0" />
        </div>

        {(daChon.length !== tatCaKetQua.length || loc.thoiLuong !== 'all') && (
          <Button variant="ghost" size="sm" className="h-9 text-xs" onClick={() => setLoc(locMacDinh(workflowId))}><X className="h-3.5 w-3.5" /> Bỏ lọc</Button>
        )}
        <div className="ml-auto flex items-center gap-1.5">
          <span className="text-[11px] text-muted-foreground">{hien.length} lần chạy</span>
          <Button variant="outline" size="sm" className="h-9" onClick={() => { void storeV2.taiLaiLuot(); setLamMoi((n) => n + 1) }}><RefreshCw className="h-3.5 w-3.5" /> Làm mới</Button>
          {hien.length > 0 && <Button variant="ghost" size="sm" className="h-9 text-xs" onClick={() => { if (confirm('Xoá toàn bộ nhật ký của luồng này?')) storeV2.clearRuns(workflowId) }}><Trash2 className="h-3.5 w-3.5" /> Xoá nhật ký</Button>}
        </div>
      </div>

      {hien.length === 0 ? (
        <EmptyState icon={History} title="Không có nhật ký hoạt động"
          description={runs.length === 0 ? 'Mở một luồng rồi bấm "Chạy thử" — mỗi lần chạy được ghi lại ở đây.' : 'Không có lần chạy nào khớp bộ lọc. Thử nới khoảng ngày hoặc chọn thêm kết quả.'} />
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-xs">
            <thead><tr className="border-b bg-muted/40 text-left">
              <th className="p-2">Bắt đầu</th>{hienTenLuong && <th className="p-2">Luồng</th>}
              <th className="p-2">Dữ liệu kích hoạt</th><th className="p-2">Bước</th><th className="p-2">Thời lượng</th><th className="p-2">Kết quả</th><th className="p-2" />
            </tr></thead>
            <tbody>
              {hien.map((r) => { const kq = ketQuaLanChay(r); return (
                <tr key={r.id} className="cursor-pointer border-t hover:bg-accent/30" onClick={() => setMo(r)}>
                  <td className="whitespace-nowrap p-2 tabular-nums text-muted-foreground"><Clock className="mr-1 inline h-3 w-3" />{fmt(r.startedAt)}</td>
                  {hienTenLuong && <td className="max-w-[16rem] truncate p-2 font-medium">{r.workflowName}</td>}
                  <td className="max-w-[22rem] truncate p-2">{r.sampleLabel}</td>
                  <td className="p-2 tabular-nums">{r.steps.filter((s) => s.status !== 'skip').length}/{r.steps.length}</td>
                  <td className="whitespace-nowrap p-2 tabular-nums text-muted-foreground">{moTaThoiLuong(r.durationMs ?? 0)}</td>
                  <td className="p-2"><Badge variant={KET_QUA_LABELS[kq].mau}>{KET_QUA_LABELS[kq].ten}</Badge></td>
                  <td className="p-2 text-right"><Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); setMo(r) }}>Chi tiết</Button></td>
                </tr>
              ) })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!mo} onOpenChange={(o) => { if (!o) setMo(null) }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>{mo?.workflowName}</DialogTitle><DialogDescription>{mo?.sampleLabel} · {fmt(mo?.startedAt)} · {moTaThoiLuong(mo?.durationMs ?? 0)}</DialogDescription></DialogHeader>
          {mo && <ChiTietLanChay run={mo} />}
        </DialogContent>
      </Dialog>
    </div>
  )
}

/** Hộp Nhật ký mở từ trình dựng luồng (tiêu đề như Lark: tên luồng | Nhật ký hoạt động). */
export function NhatKyDialog({ open, onClose, workflowId, tenLuong }: { open: boolean; onClose: () => void; workflowId: string; tenLuong: string }) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className={cn('max-w-5xl')}>
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2"><span>{tenLuong}</span><span className="text-muted-foreground">|</span><span className="font-normal">Nhật ký hoạt động</span></DialogTitle>
          <DialogDescription>Mỗi lần "Chạy thử" và (sau này) mỗi lần luồng chạy thật đều được ghi lại — bấm một dòng để xem từng bước.</DialogDescription>
        </DialogHeader>
        <NhatKyHoatDong workflowId={workflowId} hienTenLuong={false} />
      </DialogContent>
    </Dialog>
  )
}
